// @ts-check
import { extend, h } from "./dom.js";
import { icon } from "./icons.js";

/**
 * Segmented control (DS-04) for two to four mutually exclusive choices, such as
 * Board / List or Everyone / Mine. Implements the ARIA radio group pattern: one
 * tab stop, arrow keys move and select.
 *
 * @param {{ label: string, options: { value: string, text: string, icon?: string }[], value: string, onChange?: (value: string) => void }} options
 */
export function segmented({ label, options, value, onChange }) {
  const group = h("div", {
    class: "ui-segmented",
    role: "radiogroup",
    aria: { label },
  });
  /** @type {HTMLButtonElement[]} */
  const items = options.map((option) =>
    h(
      "button",
      {
        type: "button",
        role: "radio",
        class: "ui-segmented__item",
        dataset: { value: option.value },
        onClick: () => choose(option.value, true),
      },
      option.icon && icon(option.icon),
      h("span", { text: option.text }),
    ),
  );
  group.append(...items);
  let current = value;
  function render() {
    for (const item of items) {
      const on = item.dataset.value === current;
      item.setAttribute("aria-checked", String(on));
      item.tabIndex = on ? 0 : -1;
    }
  }
  /** @param {string} next @param {boolean} notify */
  function choose(next, notify) {
    if (next === current) return;
    current = next;
    render();
    if (notify) {
      onChange?.(next);
      group.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }
  group.addEventListener("keydown", (event) => {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    if (!(event.key in keys)) return;
    event.preventDefault();
    const index = items.findIndex((item) => item.dataset.value === current);
    const next =
      items[
        (index +
          keys[/** @type {keyof typeof keys} */ (event.key)] +
          items.length) %
          items.length
      ];
    choose(String(next.dataset.value), true);
    next.focus();
  });
  render();
  return extend(group, {
    get value() {
      return current;
    },
    set value(next) {
      choose(next, false);
    },
    /** @param {string} next */
    setValue(next) {
      choose(next, false);
    },
  });
}
